import { toast } from "react-toastify";
import {
  convertLabelIntoName,
  findStoredFormSignature,
  parseBooleanVal,
  resolveDynamicFieldKey,
} from "../../../Utils/utils";

export const dynamicFormInvalidSubmitToast = () => {
  toast.error(
    "Please complete the required fields on this section before continuing."
  );
};

/**
 * @returns {boolean} true when save/navigation should not proceed
 */
export const isDynamicFormSaveBlocked = ({
  isUserGracePeriodOver,
  fileUploadChecker,
  selectedBody,
  isBusy,
} = {}) => {
  if (isUserGracePeriodOver) {
    return true;
  }
  if (isBusy) {
    return true;
  }
  if (fileUploadChecker === false) {
    toast.info("Please wait for the file upload to finish.");
    return true;
  }
  if (selectedBody) {
    toast.error("Please finish the body diagram selection before saving.");
    return true;
  }
  return false;
};

export const resolveWatchFormPayload = (watchData, template) =>
  watchData?.template ? watchData : { template };

const assignFieldValueKey = (field) => {
  if (!field || field.type === "content") return field;
  const name = convertLabelIntoName(field.lable);
  if (!name) return { ...field };

  // Every field stores its id on `name` (convertLabelIntoName(label)).
  // A label of "Name" therefore uses the same key react-hook-form reads
  // for the answer. `field.name` is the id "name", not the saved value.
  // View mode reads `field.value`, which is why only edit is wrong.
  const labelKeyIsSchemaName = name === "name";

  if (field.value === undefined || field.value === null) {
    if (!labelKeyIsSchemaName) return { ...field };
    return { ...field, name: "" };
  }

  if (!labelKeyIsSchemaName && field[name] !== undefined) {
    return { ...field };
  }

  return { ...field, [name]: field.value };
};

const hydrateTableRowsForRhf = (field) => {
  if (!Array.isArray(field?.rows)) {
    return { ...field };
  }
  const rows = field.rows.map((row) => {
    const cells = Array.isArray(row)
      ? row
      : Array.isArray(row?.columns)
        ? row.columns
        : [];
    const columns = cells.map((col) => assignFieldValueKey(col));
    return { columns };
  });
  return { ...field, rows };
};

const hydrateSubSectionFieldsForRhf = (field) => {
  const subFields = field.sub_fields || [];
  const sub_section = subFields.map((sub) => {
    if (sub.type === "table") {
      return hydrateTableRowsForRhf(sub);
    }
    if (sub.type === "address" && sub.address) {
      return { ...sub, address: { ...sub.address } };
    }
    if (sub.type === "signature" || sub.type === "file") {
      return { ...sub };
    }
    return assignFieldValueKey(sub);
  });
  return { ...field, sub_section, sub_fields: subFields };
};

/**
 * Maps submitted template field `.value` props onto react-hook-form register keys.
 */
export const hydrateSubmittedTemplateForRhf = (sections) => {
  if (!Array.isArray(sections)) return [];
  return sections.map((section) => ({
    ...section,
    input_fields: (section.input_fields || []).map((field) => {
      if (field.type === "sub_section") {
        return hydrateSubSectionFieldsForRhf(field);
      }
      if (field.type === "table") {
        return hydrateTableRowsForRhf(field);
      }
      if (field.type === "address" && field.address) {
        return { ...field, address: { ...field.address } };
      }
      if (field.type === "signature" || field.type === "file") {
        return { ...field };
      }
      return assignFieldValueKey(field);
    }),
  }));
};

const signatureValidationKey = (signatureKey, inputKey) =>
  inputKey === signatureKey ? `${signatureKey}__signed` : inputKey;

/**
 * Required signatures shown as stored images do not mount the canvas validation input.
 */
export const seedStoredSignatureValidationDefaults = (
  sections,
  signatures,
  setValue,
  sectionIndex = null
) => {
  if (!setValue || !Array.isArray(sections)) return;

  const sectionIndexes =
    sectionIndex != null
      ? [sectionIndex]
      : sections.map((_, index) => index);

  sectionIndexes.forEach((si) => {
    const section = sections[si];
    if (!section?.input_fields) return;

    section.input_fields.forEach((field, fi) => {
      if (field.type === "signature" && parseBooleanVal(field.required)) {
        const inputKey = resolveDynamicFieldKey(field, fi);
        const stored = findStoredFormSignature(
          signatures,
          field.name,
          inputKey
        );
        if (stored) {
          setValue(
            signatureValidationKey(field.name, inputKey),
            "signature-added",
            { shouldValidate: true }
          );
        }
      } else if (field.type === "sub_section") {
        field.sub_fields?.forEach((sub, sf) => {
          if (sub.type !== "signature" || !parseBooleanVal(sub.required)) {
            return;
          }
          const labelKey = resolveDynamicFieldKey(sub, sf);
          const stored = findStoredFormSignature(
            signatures,
            sub.name,
            labelKey
          );
          if (stored) {
            setValue(
              signatureValidationKey(sub.name, labelKey),
              "signature-added",
              { shouldValidate: true }
            );
          }
        });
      }
    });
  });
};

export const parseSubmittedFormSignatures = (raw) => {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
